// The viewer's surface on the WEB: a plain iframe — CCT-477.
//
// ⚠️ ONLY THE SURFACE LIVES HERE. Zoom, layers, page navigation and panning all come from the
// draw.io viewer itself, which is why this file has no controls: re-implementing them would mean
// a second, poorer toolbar fighting the real one.
//
// ⚠️ THE FRAME WAS BLOCKED BY OUR OWN HEADERS, NOT BY THE READER'S BROWSER. Measured on
// 2026-10-24: Edge showed "viewer.diagrams.net a refusé de se connecter" (`#sub-frame-error`)
// while the viewer answered 200 with NO `X-Frame-Options` and NO `frame-ancestors`, even asked
// with a real frame navigation's headers, and opened fine as a top-level page. The cause is
// `staticwebapp.config.json`, which sets on EVERY route:
//     Cross-Origin-Opener-Policy: same-origin
//     Cross-Origin-Embedder-Policy: credentialless
// (cross-origin isolation, required by SharedArrayBuffer for the Python IDE terminal, CCT-174).
// Verified live on the chapter page: `crossOriginIsolated === true`. Under COEP a cross-origin
// iframe must itself assert COEP, and viewer.diagrams.net asserts none -- so it is refused.
//
// `credentialless` ON THE IFRAME is the sanctioned way out: the frame loads in a credentialless
// context, so the embedded document no longer has to assert COEP, and the page KEEPS its
// isolation (removing the global header would break the IDE terminal instead).
//
// ⚠️ THE ATTRIBUTE MUST BE SET BEFORE THE NAVIGATION, hence its position ahead of `src` in the
// props below -- React applies them in insertion order. And it is not a guarantee: an older
// engine ignores the attribute, which is exactly why the failure path below still exists.
import React from 'react';

/**
 * Did the frame fail to load the viewer?
 *
 * ⚠️ MEASURED DISCRIMINATOR, NOT A GUESS. A cross-origin document that LOADED makes
 * `contentWindow.location.href` throw a SecurityError; a blocked or failed one leaves the frame
 * on `about:blank`, which reads back without throwing. Verified both ways in a browser against
 * this very viewer and against a control page.
 */
function cadreBloque(frame: HTMLIFrameElement | null): boolean {
  if (!frame) return false;
  try {
    const href = frame.contentWindow?.location?.href;
    return href === undefined || href === 'about:blank';
  } catch {
    return false;   // SecurityError: the cross-origin document is there
  }
}

export default function DiagramFrame(
  { src, largeur, hauteur, zoom = 1, onEchec }:
  { src: string; largeur: number; hauteur: number; zoom?: number; onEchec?: () => void },
) {
  const ref = React.useRef<HTMLIFrameElement | null>(null);

  // ⚠️ THE `load` EVENT FIRES ON THE ERROR PAGE TOO, so it cannot be the signal by itself; the
  // check runs just after it. The timer covers the case where no `load` ever fires at all.
  const verifier = React.useCallback(() => {
    if (cadreBloque(ref.current)) onEchec?.();
  }, [onEchec]);

  React.useEffect(() => {
    const t = setTimeout(verifier, 6000);
    return () => clearTimeout(t);
  }, [verifier, src]);

  // ⚠️ ZOOM IS OURS TO APPLY, BECAUSE THE VIEWER HAS NONE TO GIVE. Measured 2026-10-24 in a
  // 1200x520 viewport, with and WITHOUT `fit=1`: the applied scale was 1 every time. It neither
  // shrinks a large drawing nor enlarges a small one.
  //
  // ⚠️ AND THE FRAME IS LAID OUT AT 1/zoom, THEN SCALED BACK UP. A CSS `zoom` on the element
  // alone resizes the box without telling the embedded document, so the viewer would lay out for
  // a viewport it never receives. Dividing first keeps the two in step.
  const z = Math.max(0.25, Math.min(4, zoom));
  // React Native Web passes unknown elements straight through, so a real iframe is available.
  const cadre = React.createElement('iframe', {
    ref,
    // ⚠️ FIRST, AND NOT BY STYLE: it must be on the element before `src` triggers the navigation.
    credentialless: '',
    src,
    title: 'diagramme',
    style: {
      width: largeur > 0 ? Math.round(largeur / z) : '100%',
      height: Math.round(hauteur / z),
      border: 0,
      display: 'block',
      transform: z === 1 ? undefined : `scale(${z})`,
      transformOrigin: '0 0',
    },
    // The viewer needs scripts; it needs nothing else from us.
    sandbox: 'allow-scripts allow-same-origin allow-popups',
    // ⚠️ NO `loading="lazy"`. Inside a React Native Web ScrollView the intersection test is not
    // reliable, and a diagram already on screen gains nothing from deferring its load.
    onLoad: () => setTimeout(verifier, 300),
  });

  // A scaled frame overflows its box; without clipping it would run over the text beneath it.
  return React.createElement(
    'div',
    { style: { width: '100%', height: hauteur, overflow: 'hidden' } },
    cadre,
  );
}
